import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../constants/api';
import { getAuthToken } from '../utils/auth';
import { authFetch } from '../services/api';
import { useToast } from './ToastContext';
import '../styles/components/upload-widget.css';

const UploadContext = createContext(null);

const DB_NAME = 'passio-upload-db';
const DB_VERSION = 1;
const STORE_NAME = 'uploads';

// IndexedDB helpers inside client context
function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    request.onsuccess = (e) => resolve(e.target.result);
    request.onerror = (e) => reject(e.target.error);
  });
}

async function saveUpload(data) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.put(data);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

async function deleteUpload(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(id);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

async function getAllUploads() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export const useUploadQueue = () => {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error('useUploadQueue must be used within an UploadProvider');
  }
  return context;
};

export const UploadProvider = ({ children }) => {
  const [uploads, setUploads] = useState([]);
  const [isWidgetCollapsed, setIsWidgetCollapsed] = useState(false);
  const { addToast } = useToast();
  const activeFallbackUploads = useRef({}); // Track XMLHttpRequests for manual cancelation
  const channelRef = useRef(null); // Keep track of BroadcastChannel to avoid double-closing

  // Disable Background Fetch by default - it's causing immediate failures
  // Set to true only if you want to experiment with it
  const hasBgFetch = false; // typeof window !== 'undefined' && 'BackgroundFetchManager' in window;

  // 1. Sync uploads from IndexedDB, clean up old finished ones, and bind background fetch jobs on mount
  useEffect(() => {
    const syncUploads = async () => {
      console.log('[UploadProvider] Syncing uploads from IndexedDB...');
      const stored = await getAllUploads();

      // Clean up finished uploads older than 1 hour
      const ONE_HOUR = 60 * 60 * 1000;
      const now = Date.now();
      const activeUploads = [];

      for (const upload of stored) {
        const isFinished = upload.status === 'success' || upload.status === 'failed';
        if (isFinished) {
          const age = now - (upload.completedAt || upload.createdAt || now);
          if (age > ONE_HOUR) {
            await deleteUpload(upload.id);
            continue; // Skip this upload
          }
        }
        activeUploads.push(upload);
      }

      console.log('[UploadProvider] Found', stored.length, 'stored uploads, kept', activeUploads.length);
      setUploads(activeUploads);

      // Reconnect background fetch progress listeners
      if (hasBgFetch && 'serviceWorker' in navigator) {
        try {
          const reg = await navigator.serviceWorker.ready;
          for (const upload of activeUploads) {
            console.log('[UploadProvider] Checking upload:', upload.id, 'status:', upload.status, 'isBgFetch:', upload.isBgFetch);
            if (upload.status === 'uploading' && upload.isBgFetch) {
              const bgFetchReg = await reg.backgroundFetch.get(upload.id);
              if (bgFetchReg) {
                console.log('[UploadProvider] Found active background fetch, binding progress');
                bindBgFetchProgress(bgFetchReg, upload.id);
              } else {
                console.log('[UploadProvider] No active background fetch found for upload, checking if SW updated status');
                // Give more time for SW to update status - 5 seconds instead of 1
                setTimeout(async () => {
                  const current = await getAllUploads();
                  const found = current.find(u => u.id === upload.id);
                  if (found && found.status === 'uploading') {
                    console.log('[UploadProvider] Upload still marked as uploading, marking as failed');
                    found.status = 'failed';
                    found.error = "Le téléversement s'est interrompu. Veuillez réessayer.";
                    found.currentStep = 'Interrompu';
                    await saveUpload(found);
                    setUploads(current.map(u => u.id === upload.id ? found : u));
                  }
                }, 5000);
              }
            }
          }
        } catch (err) {
          console.error('[UploadProvider] Error reconnecting Background Fetch:', err);
        }
      }
    };
    syncUploads();
  }, []);

  // 1b. Auto-dismiss finished uploads after 10 seconds from the UI
  useEffect(() => {
    const timers = uploads
      .filter(u => u.status === 'success' || u.status === 'failed')
      .map(u => setTimeout(() => {
        dismissUpload(u.id);
      }, 10000)); // 10 seconds

    return () => timers.forEach(clearTimeout);
  }, [uploads]);

  // 2. Setup BroadcastChannel listener for Service Worker updates
  useEffect(() => {
    // Only set up channel if we haven't already
    if (channelRef.current) {
      console.log('[UploadProvider] BroadcastChannel already exists, skipping setup');
      return;
    }

    console.log('[UploadProvider] Setting up BroadcastChannel listener');
    const channel = new BroadcastChannel('passio-uploads');
    channelRef.current = channel;
    
    channel.onmessage = async (event) => {
      const { id, status, album, error } = event.data;
      console.log('[UploadProvider] Received BroadcastChannel message:', event.data);
      
      setUploads(prev => prev.map(upload => {
        if (upload.id === id) {
          const updated = {
            ...upload,
            status,
            progress: status === 'success' ? 100 : (status === 'finalizing' ? 95 : upload.progress),
            currentStep: status === 'success' ? 'Terminé !' : 
                        status === 'finalizing' ? 'Enregistrement de la release...' : 
                        status === 'failed' ? 'Échec du transfert' : upload.currentStep,
            error: error || upload.error,
            albumResult: album || upload.albumResult
          };
          // Save to IndexedDB as well
          saveUpload(updated);
          return updated;
        }
        return upload;
      }));

      if (status === 'success') {
        addToast(`Release "${album?.title || 'inconnue'}" créée avec succès !`, 'success');
        // Dispatch custom event to trigger dashboard refresh
        window.dispatchEvent(new CustomEvent('passio-upload-success', { detail: { id, album } }));
      } else if (status === 'failed') {
        addToast(`Échec du téléversement : ${error}`, 'error');
      }
    };

    return () => {
      if (channelRef.current) {
        console.log('[UploadProvider] Closing BroadcastChannel');
        channelRef.current.close();
        channelRef.current = null;
      }
    };
  }, [addToast]);

  // 3. Prevent page close if there are active client-side fallback uploads
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const activeFallbacks = uploads.some(u => u.status === 'uploading' && !u.isBgFetch);
      if (activeFallbacks) {
        e.preventDefault();
        e.returnValue = 'Un téléversement est en cours. Si vous fermez cette page, il sera annulé. Voulez-vous vraiment quitter ?';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [uploads]);

  // Helper to attach progress handler to browser background fetch
  const bindBgFetchProgress = (bgFetchReg, uploadId) => {
    const updateProgress = () => {
      const percent = bgFetchReg.uploadTotal > 0
        ? Math.round((bgFetchReg.uploaded / bgFetchReg.uploadTotal) * 100)
        : 0;

      setUploads(prev => prev.map(u => {
        if (u.id === uploadId) {
          const updated = {
            ...u,
            progress: percent,
            currentStep: `Transfert des fichiers (${percent}%)`
          };
          saveUpload(updated); // Sync with IndexedDB
          return updated;
        }
        return u;
      }));
    };

    bgFetchReg.addEventListener('progress', updateProgress);
    updateProgress();
  };

  // 4. Start Upload
  const startUpload = async (releaseForm, mode = 'create', artistName = 'Artiste') => {
    const token = getAuthToken();
    if (!token) {
      addToast('Session expirée. Veuillez vous reconnecter.', 'error');
      return;
    }

    const uploadId = `upload-release-${Date.now()}`;
    
    // Construct local preview for cover
    const coverPreview = releaseForm.cover_preview || '';

    const newUpload = {
      id: uploadId,
      title: releaseForm.title,
      coverPreview,
      status: 'uploading',
      progress: 0,
      currentStep: 'Préparation...',
      error: '',
      mode,
      albumId: releaseForm.id || null,
      isBgFetch: false,
      payload: null,
      filesSaved: false,
      createdAt: Date.now()
    };

    // Save files and form to IndexedDB so they can be retrieved/retried
    const uploadDataToSave = {
      ...newUpload,
      apiBaseUrl: API_BASE_URL,
      token,
      artistName,
      // Save full form data (including files which are Blobs)
      form: {
        id: releaseForm.id,
        title: releaseForm.title,
        description: releaseForm.description,
        price_ariary: releaseForm.price_ariary,
        status: releaseForm.status,
        publication_date: releaseForm.publication_date,
        type: releaseForm.type,
        is_free: releaseForm.is_free,
        cover_url: releaseForm.cover_url,
        cover_storage_provider: releaseForm.cover_storage_provider,
        cover_storage_key: releaseForm.cover_storage_key,
        cover_file: releaseForm.cover_file,
        tracks: releaseForm.tracks.map(t => ({
          id: t.id,
          title: t.title,
          duration: t.duration,
          position: t.position,
          encrypted_audio_url: t.encrypted_audio_url,
          preview_url: t.preview_url,
          audio_storage_provider: t.audio_storage_provider,
          audio_storage_key: t.audio_storage_key,
          lyrics_storage_key: t.lyrics_storage_key,
          lyrics_url: t.lyrics_url,
          has_lyrics: t.has_lyrics,
          file: t.file, // Stored as Blob/File in DB
          lyrics_file: t.lyrics_file // Stored as Blob/File in DB
        }))
      }
    };

    // Prepare API request payload skeleton
    uploadDataToSave.payload = {
      title: releaseForm.title,
      description: releaseForm.description,
      price_ariary: releaseForm.is_free ? 0 : releaseForm.price_ariary,
      status: releaseForm.status,
      publication_date: releaseForm.publication_date,
      type: releaseForm.type,
      is_free: !!releaseForm.is_free,
      cover_url: releaseForm.cover_url,
      cover_storage_provider: releaseForm.cover_storage_provider,
      cover_storage_key: releaseForm.cover_storage_key,
      tracks: releaseForm.tracks.map(t => ({
        title: t.title,
        encrypted_audio_url: t.encrypted_audio_url,
        preview_url: t.preview_url,
        audio_storage_provider: t.audio_storage_provider,
        audio_storage_key: t.audio_storage_key,
        lyrics_storage_key: t.lyrics_storage_key,
        lyrics_url: t.lyrics_url,
        has_lyrics: t.has_lyrics,
        // 🔵 media_file_id : réutilisation de fichier audio
        media_file_id: t.media_file_id || null
      }))
    };

    // Try to request notification permissions early if using background fetch
    if (hasBgFetch && window.Notification && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    if (hasBgFetch && 'serviceWorker' in navigator) {
      // Modern Background Fetch Mode
      try {
        const reg = await navigator.serviceWorker.ready;
        const requests = [];

        // Cover Request
        if (releaseForm.cover_file) {
          const coverFormData = new FormData();
          coverFormData.append('file', releaseForm.cover_file);
          coverFormData.append('album_title', releaseForm.title);
          coverFormData.append('artist_id', releaseForm.artist_id || '');
          
          requests.push(new Request(`${API_BASE_URL}/api/upload/cover`, {
            method: 'POST',
            body: coverFormData,
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-Upload-Type': 'cover'
            }
          }));
        }

        // Tracks Audio Requests
        for (let i = 0; i < releaseForm.tracks.length; i++) {
          const track = releaseForm.tracks[i];
          if (track.file) {
            const trackLabel = releaseForm.type === 'single' ? releaseForm.title : (track.title || track.file.name);
            const audioFormData = new FormData();
            audioFormData.append('file', track.file);
            audioFormData.append('album_title', releaseForm.title);
            audioFormData.append('track_title', `${artistName} - ${trackLabel}`);
            if (releaseForm.id) {
              audioFormData.append('album_id', releaseForm.id);
            }

            requests.push(new Request(`${API_BASE_URL}/api/upload/audio`, {
              method: 'POST',
              body: audioFormData,
              headers: {
                'Authorization': `Bearer ${token}`,
                'X-Upload-Type': 'track',
                'X-Track-Index': String(i)
              }
            }));
          }
        }

        // Tracks Lyrics Requests
        for (let i = 0; i < releaseForm.tracks.length; i++) {
          const track = releaseForm.tracks[i];
          if (track.lyrics_file) {
            const lyricsLabel = releaseForm.type === 'single' ? releaseForm.title : (track.title || track.lyrics_file.name);
            const lyricsFormData = new FormData();
            lyricsFormData.append('file', track.lyrics_file);
            lyricsFormData.append('album_title', releaseForm.title);
            lyricsFormData.append('track_title', `${artistName} - ${lyricsLabel}`);

            requests.push(new Request(`${API_BASE_URL}/api/upload/lyrics`, {
              method: 'POST',
              body: lyricsFormData,
              headers: {
                'Authorization': `Bearer ${token}`,
                'X-Upload-Type': 'lyrics',
                'X-Lyrics-Track-Index': String(i)
              }
            }));
          }
        }

        // Check if we have any requests to upload
        if (requests.length === 0) {
          console.log('[UploadProvider] No files to upload, proceeding directly to save release');
          uploadDataToSave.isBgFetch = false;
          await saveUpload(uploadDataToSave);
          setUploads(prev => [...prev, uploadDataToSave]);
          setIsWidgetCollapsed(false);
          runClientUpload(uploadId, uploadDataToSave, token);
          return;
        }

        // Trigger browser Background Fetch
        console.log('[UploadProvider] Starting Background Fetch with requests:', requests.length);
        uploadDataToSave.isBgFetch = true;
        await saveUpload(uploadDataToSave);
        
        setUploads(prev => [...prev, uploadDataToSave]);
        setIsWidgetCollapsed(false);

        // Start background fetch and handle immediate failures
        let bgFetchReg;
        try {
          bgFetchReg = await reg.backgroundFetch.fetch(uploadId, requests, {
            title: `Upload : ${releaseForm.title}`,
            icons: [
              {
                src: '/favicon.svg',
                sizes: '192x192',
                type: 'image/svg+xml'
              }
            ]
          });
        } catch (bgFetchErr) {
          console.error('[UploadProvider] Background Fetch failed to initialize:', bgFetchErr);
          throw bgFetchErr; // Will be caught by outer catch and fall back to client upload
        }

        bindBgFetchProgress(bgFetchReg, uploadId);
        addToast(`Téléversement en arrière-plan démarré pour "${releaseForm.title}".`, 'info');
        return;
      } catch (err) {
        console.warn('[UploadProvider] Background Fetch failed to start, falling back to client-side upload:', err);
        // Clean up if we saved the upload but background fetch failed
        try {
          await deleteUpload(uploadId);
          setUploads(prev => prev.filter(u => u.id !== uploadId));
        } catch (cleanupErr) {
          console.warn('[UploadProvider] Cleanup failed:', cleanupErr);
        }
        // Fallback to client-side upload
      }
    }

    // Client-side sequential upload fallback (Firefox / Safari or when SW ready fails)
    uploadDataToSave.isBgFetch = false;
    await saveUpload(uploadDataToSave);
    
    setUploads(prev => [...prev, uploadDataToSave]);
    setIsWidgetCollapsed(false);
    
    runClientUpload(uploadId, uploadDataToSave, token);
  };

  // 5. Client-Side Upload Implementation (Fallback)
  const runClientUpload = async (uploadId, uploadData, token) => {
    const { form, payload } = uploadData;
    const updateLocalStatus = async (step, progress, status = 'uploading', err = '') => {
      setUploads(prev => prev.map(u => {
        if (u.id === uploadId) {
          const updated = {
            ...u,
            currentStep: step,
            progress,
            status,
            error: err
          };
          saveUpload(updated);
          return updated;
        }
        return u;
      }));
    };

    try {
      let coverUrl = form.cover_url;
      let coverStorageProvider = form.cover_storage_provider;
      let coverStorageKey = form.cover_storage_key;

      const uploadMediaProgressXHR = (path, file, fields, onProgress) => {
        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          activeFallbackUploads.current[uploadId] = xhr;
          
          const formData = new FormData();
          formData.append('file', file);
          Object.entries(fields).forEach(([k, v]) => {
            if (v !== undefined && v !== null) formData.append(k, String(v));
          });

          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable && onProgress) {
              onProgress(Math.round((e.loaded / e.total) * 100));
            }
          });

          xhr.addEventListener('load', () => {
            let res = {};
            try { res = JSON.parse(xhr.responseText); } catch { res = { message: 'Erreur déchiffrage serveur' }; }
            if (xhr.status >= 200 && xhr.status < 300) resolve(res);
            else reject(new Error(res.message || 'Erreur d\'upload'));
          });
          xhr.addEventListener('error', () => reject(new Error('Erreur réseau')));
          xhr.addEventListener('abort', () => reject(new Error('Upload annulé')));

          xhr.open('POST', `${API_BASE_URL}${path}`);
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          xhr.send(formData);
        });
      };

      // A. Upload Cover
      if (form.cover_file) {
        await updateLocalStatus('Envoi de la pochette...', 5);
        const res = await uploadMediaProgressXHR('/api/upload/cover', form.cover_file, { album_title: form.title }, (percent) => {
          updateLocalStatus('Envoi de la pochette...', Math.round(5 + percent * 0.15)); // max 20%
        });
        coverUrl = res.url;
        coverStorageProvider = res.provider || 'cloudinary';
        coverStorageKey = res.storage_key || res.public_id || '';
      }
      payload.cover_url = coverUrl;
      payload.cover_storage_provider = coverStorageProvider;
      payload.cover_storage_key = coverStorageKey;

      // B. Upload Tracks
      const tracksToUpload = form.tracks.filter(t => t.file);
      const totalTracks = tracksToUpload.length;

      for (let i = 0; i < form.tracks.length; i++) {
        const trackRow = form.tracks[i];
        
        if (!trackRow.file) {
          // Already uploaded track, keep metadata
          payload.tracks[i] = {
            title: trackRow.title,
            encrypted_audio_url: trackRow.encrypted_audio_url,
            preview_url: trackRow.preview_url,
            audio_storage_provider: trackRow.audio_storage_provider,
            audio_storage_key: trackRow.audio_storage_key,          lyrics_storage_key: trackRow.lyrics_storage_key,
          lyrics_url: trackRow.lyrics_url,
          has_lyrics: trackRow.has_lyrics,
          media_file_id: trackRow.media_file_id || null
        };
          continue;
        }

        const idxInUploads = tracksToUpload.indexOf(trackRow);
        const trackLabel = form.type === 'single' ? form.title : (trackRow.title || trackRow.file.name);
        await updateLocalStatus(`Envoi de "${trackLabel}"...`, 20 + idxInUploads * (60 / totalTracks));

        const res = await uploadMediaProgressXHR('/api/upload/audio', trackRow.file, {
          album_title: form.title,
          track_title: `${uploadData.artistName} - ${trackLabel}`,
          album_id: form.id || undefined
        }, (percent) => {
          const base = 20 + idxInUploads * (60 / totalTracks);
          const weight = 60 / totalTracks;
          const current = Math.round(base + (percent * weight / 100));
          updateLocalStatus(`Envoi de "${trackLabel}" (${percent}%)...`, current);
        });

        payload.tracks[i] = {
          title: trackRow.title || trackRow.file.name.replace(/\.[^.]+$/, ''),
          encrypted_audio_url: res.url,
          preview_url: res.url,
          audio_storage_provider: res.provider || 'backblaze-b2',
          audio_storage_key: res.storage_key || '',
          lyrics_storage_key: trackRow.lyrics_storage_key || null,
          lyrics_url: trackRow.lyrics_url || null,
          has_lyrics: trackRow.has_lyrics || false,
          duration: res.duration || trackRow.duration,
          // 🔵 media_file_id : réutilisation de fichier audio
          media_file_id: res.media_file_id || null
        };
      }

      // C. Upload Lyrics
      for (let i = 0; i < form.tracks.length; i++) {
        const trackRow = form.tracks[i];
        if (trackRow.lyrics_file) {
          const lyricsLabel = form.type === 'single' ? form.title : (trackRow.title || trackRow.lyrics_file.name);
          await updateLocalStatus(`Envoi des paroles pour "${lyricsLabel}"...`, 80);
          
          try {
            const formData = new FormData();
            formData.append('file', trackRow.lyrics_file);
            formData.append('album_title', form.title);
            formData.append('track_title', `${uploadData.artistName} - ${lyricsLabel}`);

            const response = await authFetch('/api/upload/lyrics', {
              method: 'POST',
              body: formData
            });
            const res = await response.json();
            if (!response.ok) throw new Error(res.message);

            payload.tracks[i].lyrics_storage_key = res.storage_key || '';
            payload.tracks[i].lyrics_url = res.url || null;
            payload.tracks[i].has_lyrics = true;
          } catch (lyricsErr) {
            console.warn('LRC upload failed, continuing anyway:', lyricsErr);
          }
        }
      }

      // D. Final Save Request
      await updateLocalStatus('Enregistrement de la release...', 90);
      const apiPath = uploadData.mode === 'create'
        ? '/api/artists/me/albums'
        : `/api/artists/me/albums/${uploadData.albumId}`;
      const method = uploadData.mode === 'create' ? 'POST' : 'PUT';

      const apiResponse = await authFetch(apiPath, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const apiResult = await apiResponse.json();

      if (!apiResponse.ok) {
        throw new Error(apiResult.message || 'Le serveur a rejeté la création.');
      }

      // Success — single atomic update with IndexedDB persistence
      setUploads(prev => prev.map(u => {
        if (u.id === uploadId) {
          const updated = { ...u, albumResult: apiResult, status: 'success', progress: 100, currentStep: 'Terminé !', completedAt: Date.now() };
          saveUpload(updated);
          return updated;
        }
        return u;
      }));
      addToast(`Release "${payload.title}" créée avec succès !`, 'success');
      
      // Dispatch refresh
      window.dispatchEvent(new CustomEvent('passio-upload-success', { detail: { id: uploadId, album: apiResult } }));
      
      delete activeFallbackUploads.current[uploadId];
    } catch (err) {
      console.error('[UploadProvider] Fallback upload failed:', err);
      const errMsg = err.message || "Une erreur est survenue lors de l'upload.";
      // Failure — single atomic update with IndexedDB persistence
      setUploads(prev => prev.map(u => {
        if (u.id === uploadId) {
          const updated = { ...u, status: 'failed', progress: 100, currentStep: 'Échec', error: errMsg, completedAt: Date.now() };
          saveUpload(updated);
          return updated;
        }
        return u;
      }));
      addToast(`Échec du téléversement: ${errMsg}`, 'error');
      
      delete activeFallbackUploads.current[uploadId];
    }
  };

  // 6. Retry Upload
  const retryUpload = async (id) => {
    console.log('[UploadProvider] Retrying upload:', id);
    const list = [...uploads];
    const upload = list.find(u => u.id === id);
    if (!upload) return;

    // Set back to uploading
    setUploads(prev => prev.map(u => u.id === id ? { ...u, status: 'uploading', progress: 0, currentStep: 'Préparation du nouvel essai...', error: '' } : u));
    
    const dbData = await getAllUploads();
    const storedObject = dbData.find(u => u.id === id);
    
    if (storedObject && storedObject.form) {
      // Always try with client-side upload first on retry for more reliability
      // Or give user choice, but for simplicity we'll use client-side directly
      console.log('[UploadProvider] Starting client-side retry upload');
      try {
        await deleteUpload(id);
        setUploads(prev => prev.filter(u => u.id !== id));
        // Create a new upload ID for the retry
        const newUploadData = { ...storedObject };
        startUpload(newUploadData.form, newUploadData.mode, newUploadData.artistName);
      } catch (e) {
        console.error('[UploadProvider] Failed to retry upload:', e);
        addToast("Échec de la tentative de relance. Veuillez réessayer.", "error");
      }
    } else {
      addToast("Impossible de relancer : données de fichiers perdues. Veuillez recréer la release.", "error");
      dismissUpload(id);
    }
  };

  // 7. Dismiss Upload from Widget
  const dismissUpload = async (id) => {
    // If it's a running fallback upload, abort the request first
    if (activeFallbackUploads.current[id]) {
      activeFallbackUploads.current[id].abort();
      delete activeFallbackUploads.current[id];
    }

    // Cancel browser background fetch if running
    if (hasBgFetch && 'serviceWorker' in navigator) {
      try {
        const reg = await navigator.serviceWorker.ready;
        const bgFetchReg = await reg.backgroundFetch.get(id);
        if (bgFetchReg && bgFetchReg.result === '') {
          await bgFetchReg.abort();
        }
      } catch (e) {
        console.warn('Failed to abort Background Fetch registration:', e);
      }
    }

    await deleteUpload(id);
    setUploads(prev => prev.filter(u => u.id !== id));
  };

  const activeUploads = uploads.filter(u => u.status === 'uploading');

  return (
    <UploadContext.Provider value={{ uploads, startUpload, retryUpload, dismissUpload }}>
      {children}

      {/* Floating Upload Widget */}
      {uploads.length > 0 && (
        <div className={`passio-upload-widget ${isWidgetCollapsed ? 'collapsed' : ''}`}>
          {/* Header */}
          <div className="widget-header d-flex align-items-center justify-content-between" onClick={() => setIsWidgetCollapsed(!isWidgetCollapsed)}>
            <div className="d-flex align-items-center gap-2">
              <div className="status-spinner-container">
                {activeUploads.length > 0 ? (
                  <i className="fa-solid fa-circle-notch fa-spin text-purple"></i>
                ) : (
                  <i className="fa-solid fa-cloud-arrow-up text-muted"></i>
                )}
              </div>
              <span className="widget-title fw-medium">
                {activeUploads.length > 0 
                  ? `Téléversement en cours (${activeUploads.length})` 
                  : 'Transferts terminés'}
              </span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <button className="btn-icon" onClick={(e) => { e.stopPropagation(); setIsWidgetCollapsed(!isWidgetCollapsed); }}>
                <i className={`fa-solid ${isWidgetCollapsed ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
              </button>
            </div>
          </div>

          {/* Body */}
          {!isWidgetCollapsed && (
            <div className="widget-body">
              {uploads.map((upload) => (
                <div key={upload.id} className="upload-item">
                  <div className="d-flex gap-3">
                    {/* Cover Art Preview */}
                    <div className="upload-cover-art">
                      {upload.coverPreview ? (
                        <img src={upload.coverPreview} alt="cover" />
                      ) : (
                        <div className="cover-placeholder">
                          <i className="fa-solid fa-music"></i>
                        </div>
                      )}
                    </div>

                    {/* Progress Info */}
                    <div className="flex-fill min-w-0">
                      <div className="d-flex align-items-start justify-content-between gap-2">
                        <h4 className="upload-release-title text-truncate mb-0 fw-semibold">{upload.title}</h4>
                        <div className="d-flex align-items-center gap-1">
                          {upload.status === 'success' && (
                            <i className="fa-solid fa-circle-check text-success fs-5"></i>
                          )}
                          {upload.status === 'failed' && (
                            <i className="fa-solid fa-circle-exclamation text-danger fs-5"></i>
                          )}
                          {upload.status === 'uploading' && (
                            <span className="percentage-badge fw-bold text-purple">{upload.progress}%</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="current-step text-muted text-truncate mb-2">{upload.currentStep}</div>

                      {/* Progress bar */}
                      {upload.status === 'uploading' && (
                        <div className="progress-bar-container">
                          <div 
                            className="progress-bar-fill" 
                            style={{ width: `${upload.progress}%` }}
                          ></div>
                        </div>
                      )}

                      {/* Error info */}
                      {upload.status === 'failed' && (
                        <div className="error-message text-danger mb-2">
                          {upload.error || "Une erreur est survenue."}
                        </div>
                      )}

                      {/* Action buttons */}
                      <div className="upload-actions d-flex gap-2 justify-content-end mt-2">
                        {upload.status === 'failed' && (
                          <button className="btn btn-xs btn-purple-glow rounded-pill" onClick={() => retryUpload(upload.id)}>
                            <i className="fa-solid fa-rotate-right me-1"></i> Réessayer
                          </button>
                        )}
                        <button className="btn btn-xs btn-outline-light rounded-pill border-muted text-muted" onClick={() => dismissUpload(upload.id)}>
                          {upload.status === 'uploading' ? 'Annuler' : 'Effacer'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </UploadContext.Provider>
  );
};
